// ─────────────────────────────────────────────────────────────
// ZIFEK HÉBERGEMENT — types, constantes et utilitaires partagés
// Module natif Zifek : hôtels, riads, maisons d'hôtes, appartements,
// villas, studios, chambres… Chaque donnée est scopée par `merchant_id`.
// ─────────────────────────────────────────────────────────────

export type PropertyType =
  | 'apartment' | 'villa' | 'house' | 'riad' | 'hotel'
  | 'guesthouse' | 'studio' | 'room' | 'residence' | 'other';

export type UnitType = 'bedroom' | 'room' | 'suite' | 'studio' | 'apartment' | 'villa' | 'other';

export type BookingMode = 'ENTIRE_PROPERTY' | 'UNITS_ONLY' | 'ENTIRE_PROPERTY_AND_UNITS';

export type PropertyStatus = 'draft' | 'active' | 'inactive';

export type ReservationStatus =
  | 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled';

export type PaymentStatus = 'unpaid' | 'partial' | 'paid' | 'refunded';

export type ReservationSource =
  | 'zifek_website' | 'ai_assistant' | 'ai_chat' | 'manual'
  | 'phone' | 'whatsapp' | 'external_platform' | 'other';

export interface HebergementProperty {
  id: number;
  merchant_id: number;
  owner_id: number | null;
  name: string;
  type: PropertyType;
  description: string | null;
  address: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  capacity: number;
  bed_count: number;
  bathroom_count: number;
  price: number;
  currency: string;
  booking_mode: BookingMode;
  status: PropertyStatus;
  published: boolean;
  /** Bien mis en avant (étoile) : apparaît en premier sur le site public. */
  featured?: boolean;
  cover_image: string | null;
  created_at: string;
  updated_at: string;
}

export interface HebergementUnit {
  id: number;
  property_id: number;
  merchant_id: number;
  name: string;
  type: UnitType;
  description: string | null;
  capacity: number;
  beds: number;
  bathrooms: number;
  price: number;
  status: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

export interface HebergementMedia {
  id: number;
  property_id: number | null;
  unit_id: number | null;
  merchant_id: number;
  url: string;
  type: string;
  caption: string | null;
  sort_order: number;
  is_cover: boolean;
  created_at: string;
}

export interface HebergementAmenity {
  id: number;
  property_id: number | null;
  unit_id: number | null;
  merchant_id: number;
  name: string;
  icon: string | null;
  created_at: string;
}

/**
 * Type d'hébergement — hébergé dans la table `typehebergement` pour être
 * entièrement personnalisable par chaque marchand. `idcommerce = null` désigne
 * un type global par défaut (Appartement, Villa, Riad…), utilisable par tout le
 * monde. `parent_id` permet de créer des sous-types (ex. Villa → Villa vue mer).
 */
export interface HebergementCategory {
  id: number;
  idcommerce: number | null;
  nom: string;
  slug: string;
  icon: string | null;
  image_couverture: string | null;
  parent_id: number | null;
  sort_order: number;
  created_at: string;
}

export interface CategoryInput {
  nom: string;
  parent_id?: number | null;
  icon?: string | null;
  image_couverture?: string | null;
}

export interface HebergementBlock {
  id: number;
  property_id: number;
  unit_id: number | null;
  merchant_id: number;
  start_date: string;
  end_date: string;
  reason: string | null;
  created_by: number | null;
  created_at: string;
}

export interface HebergementReservation {
  id: number;
  merchant_id: number;
  property_id: number;
  unit_id: number | null;
  customer_id: number | null;
  guest_name: string | null;
  guest_email: string | null;
  guest_phone: string | null;
  check_in: string;
  check_out: string;
  guests: number;
  status: ReservationStatus;
  price_per_night: number;
  nights: number;
  subtotal: number;
  discount: number;
  fees: number;
  total: number;
  currency: string;
  payment_status: PaymentStatus;
  source: ReservationSource;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface HebergementReservationPayment {
  id: number;
  reservation_id: number;
  merchant_id: number | null;
  amount: number;
  method: string | null;
  provider: string | null;
  payment_id: string | null;
  status: string;
  created_at: string;
}

// ── Constantes ───────────────────────────────────────────────

export const PROPERTY_TYPES: { value: PropertyType; label: string; icon: string }[] = [
  { value: 'apartment', label: 'Appartement', icon: 'ri-building-4-line' },
  { value: 'villa', label: 'Villa', icon: 'ri-home-5-line' },
  { value: 'house', label: 'Maison', icon: 'ri-home-4-line' },
  { value: 'riad', label: 'Riad', icon: 'ri-hotel-line' },
  { value: 'hotel', label: 'Hôtel', icon: 'ri-building-2-line' },
  { value: 'guesthouse', label: "Maison d'hôtes", icon: 'ri-hotel-bed-line' },
  { value: 'studio', label: 'Studio', icon: 'ri-door-line' },
  { value: 'room', label: 'Chambre', icon: 'ri-hotel-bed-line' },
  { value: 'residence', label: 'Résidence', icon: 'ri-community-line' },
  { value: 'other', label: 'Autre', icon: 'ri-more-line' },
];

export const UNIT_TYPES: { value: UnitType; label: string }[] = [
  { value: 'room', label: 'Chambre' },
  { value: 'bedroom', label: 'Chambre à coucher' },
  { value: 'suite', label: 'Suite' },
  { value: 'studio', label: 'Studio' },
  { value: 'apartment', label: 'Appartement' },
  { value: 'villa', label: 'Villa' },
  { value: 'other', label: 'Autre' },
];

export const BOOKING_MODES: { value: BookingMode; label: string; description: string }[] = [
  { value: 'ENTIRE_PROPERTY', label: 'Je loue le bien entier', description: 'Le bien complet est réservable (villa, appartement entier…).' },
  { value: 'UNITS_ONLY', label: 'Chambre par chambre', description: 'Chaque chambre/unité se réserve séparément.' },
  { value: 'ENTIRE_PROPERTY_AND_UNITS', label: 'Les deux', description: 'Le bien entier OU chaque chambre peut être réservé.' },
];

export const RESERVATION_STATUS_META: Record<ReservationStatus, { label: string; className: string; icon: string }> = {
  pending: { label: 'En attente', className: 'bg-amber-100 text-amber-700', icon: 'ri-time-line' },
  confirmed: { label: 'Confirmée', className: 'bg-accent-100 text-accent-700', icon: 'ri-checkbox-circle-line' },
  checked_in: { label: 'Arrivé', className: 'bg-primary-100 text-primary-700', icon: 'ri-login-box-line' },
  checked_out: { label: 'Parti', className: 'bg-background-200 text-foreground-700', icon: 'ri-logout-box-line' },
  cancelled: { label: 'Annulée', className: 'bg-red-100 text-red-600', icon: 'ri-close-circle-line' },
};

export const PAYMENT_STATUS_META: Record<PaymentStatus, { label: string; className: string }> = {
  unpaid: { label: 'Non payé', className: 'bg-red-100 text-red-600' },
  partial: { label: 'Partiel', className: 'bg-amber-100 text-amber-700' },
  paid: { label: 'Payé', className: 'bg-accent-100 text-accent-700' },
  refunded: { label: 'Remboursé', className: 'bg-background-200 text-foreground-700' },
};

export const RESERVATION_SOURCES: { value: ReservationSource; label: string }[] = [
  { value: 'zifek_website', label: 'Site Zifek' },
  { value: 'ai_assistant', label: 'Assistant IA' },
  { value: 'ai_chat', label: 'Chat IA' },
  { value: 'manual', label: 'Manuel' },
  { value: 'phone', label: 'Téléphone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'external_platform', label: 'Plateforme externe' },
  { value: 'other', label: 'Autre' },
];

export const DEFAULT_AMENITIES: string[] = [
  'Piscine', 'Parking', 'Wi-Fi', 'Climatisation', 'Cuisine', 'Petit déjeuner',
  'Animal accepté', 'Bord de mer', 'Centre-ville', 'Salle de bain privée',
  'Terrasse', 'Jardin', 'TV', 'Lave-linge', 'Ascenseur', 'Salle de sport',
];

// ── Utilitaires ──────────────────────────────────────────────

export function propertyTypeMeta(type: string | null | undefined) {
  return PROPERTY_TYPES.find((t) => t.value === type) || PROPERTY_TYPES[PROPERTY_TYPES.length - 1];
}

/**
 * Métadonnées d'une catégorie d'hébergement.
 * Cherche d'abord dans la liste DB fournie (par slug), sinon retombe sur les
 * types par défaut, sinon sur « Autre ».
 */
export function categoryMeta(
  categories: HebergementCategory[],
  slug: string | null | undefined,
): { slug: string; label: string; icon: string } {
  const fromDb = slug ? categories.find((c) => c.slug === slug) : undefined;
  if (fromDb) return { slug: fromDb.slug, label: fromDb.nom, icon: fromDb.icon || 'ri-building-4-line' };
  const fallback = propertyTypeMeta(slug);
  return { slug: fallback.value, label: fallback.label, icon: fallback.icon };
}

/**
 * Ordonne les types d'hébergement : parents d'abord puis leurs sous-types,
 * pour un affichage en liste (déroulante, filtres…).
 */
export function orderedCategories(categories: HebergementCategory[]): { cat: HebergementCategory; isChild: boolean }[] {
  const roots = categories.filter((c) => !c.parent_id || c.parent_id === 0);
  const used = new Set<number>();
  const list: { cat: HebergementCategory; isChild: boolean }[] = [];
  const byOrder = (a: HebergementCategory, b: HebergementCategory) =>
    (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
  roots.sort(byOrder).forEach((r) => {
    list.push({ cat: r, isChild: false });
    used.add(r.id);
    categories
      .filter((c) => c.parent_id === r.id)
      .sort(byOrder)
      .forEach((k) => { list.push({ cat: k, isChild: true }); used.add(k.id); });
  });
  categories.filter((c) => !used.has(c.id)).sort(byOrder).forEach((c) => list.push({ cat: c, isChild: false }));
  return list;
}

/** Transforme un libellé en slug hostname-safe (pour une catégorie custom). */
export function slugifyCategory(label: string): string {
  const base = label
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return base || 'type';
}

export function unitTypeLabel(type: string | null | undefined): string {
  return UNIT_TYPES.find((t) => t.value === type)?.label || 'Unité';
}

export function bookingModeMeta(mode: string | null | undefined) {
  return BOOKING_MODES.find((m) => m.value === mode) || BOOKING_MODES[0];
}

/** Nombre de nuits entre deux dates (check-out exclus). */
export function nightsBetween(checkIn: string, checkOut: string): number {
  if (!checkIn || !checkOut) return 0;
  const a = new Date(`${checkIn}T00:00:00`).getTime();
  const b = new Date(`${checkOut}T00:00:00`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86400000));
}

/** Total d'un séjour : nuits × prix − remise + frais. */
export function computeTotal(pricePerNight: number, checkIn: string, checkOut: string, discount = 0, fees = 0): number {
  const nights = nightsBetween(checkIn, checkOut);
  return Math.max(0, pricePerNight * nights - (discount || 0) + (fees || 0));
}

export function formatMoney(amount: number, currency = 'MAD'): string {
  const value = Number.isFinite(amount) ? amount : 0;
  const formatted = value.toLocaleString('fr-FR', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${formatted} ${currency}`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export function todayISO(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function addDaysISO(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Deux intervalles [aStart, aEnd) et [bStart, bEnd) se chevauchent-ils ? */
export function rangesOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return aStart < bEnd && aEnd > bStart;
}

const ACTIVE_STATUSES: ReservationStatus[] = ['pending', 'confirmed', 'checked_in'];

/**
 * Vérification de disponibilité côté client (miroir du moteur serveur).
 * Sert à l'UI (aperçu, formulaire) — la VÉRITÉ reste la fonction serveur
 * `hebergement_check_availability` appelée à l'enregistrement.
 */
export function isAvailableClientSide(
  propertyId: number,
  unitId: number | null,
  checkIn: string,
  checkOut: string,
  reservations: HebergementReservation[],
  blocks: HebergementBlock[],
): boolean {
  if (!checkIn || !checkOut || checkOut <= checkIn) return false;

  const conflictingRes = reservations.some((r) => {
    if (r.property_id !== propertyId) return false;
    if (!ACTIVE_STATUSES.includes(r.status)) return false;
    if (!rangesOverlap(checkIn, checkOut, r.check_in, r.check_out)) return false;
    if (unitId === null) return true;
    return r.unit_id === null || r.unit_id === unitId;
  });
  if (conflictingRes) return false;

  const conflictingBlock = blocks.some((b) => {
    if (b.property_id !== propertyId) return false;
    if (!rangesOverlap(checkIn, checkOut, b.start_date, b.end_date)) return false;
    if (unitId === null) return true;
    return b.unit_id === null || b.unit_id === unitId;
  });
  if (conflictingBlock) return false;

  return true;
}

/** Prix à facturer pour un couple (bien, unité). */
export function priceFor(property: HebergementProperty, unit: HebergementUnit | null): number {
  if (unit) return Number(unit.price) || 0;
  return Number(property.price) || 0;
}

/** Options réservables d'un bien selon son mode de réservation. */
export function bookableOptions(
  property: HebergementProperty,
  units: HebergementUnit[],
): { value: string; label: string; unitId: number | null; price: number }[] {
  const list: { value: string; label: string; unitId: number | null; price: number }[] = [];
  if (property.booking_mode !== 'UNITS_ONLY') {
    list.push({
      value: 'property',
      label: `${property.name} — bien entier`,
      unitId: null,
      price: Number(property.price) || 0,
    });
  }
  if (property.booking_mode !== 'ENTIRE_PROPERTY') {
    units
      .filter((u) => u.property_id === property.id && u.status === 'active')
      .forEach((u) => list.push({ value: `unit-${u.id}`, label: u.name, unitId: u.id, price: Number(u.price) || 0 }));
  }
  return list;
}